import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Linkedin,
  ArrowUp,
  Clock,
  Award,
  Users,
  Globe,
  Send,
  CheckCircle,
  ExternalLink
} from "lucide-react";
import logo from '../Assests/lumbini-logo.jpg'

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  const footerLinks = {
    company: [
      { name: 'Home', path: '/' },
      { name: 'About Us', path: '/about' },
      { name: 'Services', path: '/services' },
      { name: 'Projects', path: '/projects' },
      { name: 'Jobs', path: '/jobs' },
    ],
    services: [
      { name: 'NGO Consultancy', path: '/services' },
      { name: 'HR Solutions', path: '/services' },
      { name: 'Research & Studies', path: '/services' },
      { name: 'Technical Assistance', path: '/services' },
      { name: 'Supply Chain Management', path: '/services' },
    ],
    support: [
      { name: 'Contact Us', path: '/contactus' },
      { name: 'Privacy Policy', path: '#' },
      { name: 'Terms of Service', path: '#' },
      { name: 'Cookie Policy', path: '#' },
      { name: 'Sitemap', path: '#' },
    ]
  };

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/lumconser',  label: 'Facebook', color: 'hover:text-blue-400' },
    { icon: Linkedin, href: '#', label: 'LinkedIn', color: 'hover:text-blue-500' },
  ];



  return (
    <footer className="bg-gradient-to-br from-slate-50 to-gray-100 text-gray-900 relative overflow-hidden border-t border-gray-200">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%236366f1' fill-opacity='0.05'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="container-custom relative z-10 px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="pt-6 sm:pt-8 pb-4">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8 sm:gap-12 max-w-6xl mx-auto">
            {/* Company Logo & Social */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-1 space-y-8"
            >
              {/* Company Logo */}
              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                  <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
                    <div className="relative">
                      <img
                        className="h-8 w-auto sm:h-12 sm:w-auto transition-transform duration-300 group-hover:scale-105"
                        src={logo}
                        alt="Lumbini Consultancy Logo"
                      />
                    </div>

                  </Link>

                </div>
                <p className="text-gray-700 text-xs sm:text-sm leading-relaxed max-w-xs mx-auto lg:mx-0">
                  Excellence in consulting services with 8+ years of experience in transforming businesses.
                </p>
              </div>

              {/* Social Media Links */}
              <div className="text-center lg:text-left">
                <h5 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Follow Us</h5>
                <div className="flex justify-center lg:justify-start space-x-2 sm:space-x-3">
                  {socialLinks.map((social, index) => (
                    <motion.a
                    target="_blank"
                      key={index}
                      href={social.href}
                      className="w-10 h-10 sm:w-12 sm:h-12 bg-white hover:bg-primary-50 border border-gray-200 hover:border-primary-200 rounded-xl flex items-center justify-center transition-all duration-300 group shadow-sm hover:shadow-md"
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label={social.label}
                    >
                      <social.icon className="h-5 w-5 text-gray-600 group-hover:text-primary-600 transition-colors duration-300" />
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Company Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="space-y-6 text-center"
            >
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-6 relative">
                  Company
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-primary-600 to-accent-600 rounded-full"></div>
                </h4>
                <ul className="space-y-3">
                  {footerLinks.company.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.path}
                        className="text-gray-700 hover:text-primary-600 transition-colors duration-300 text-sm flex items-center justify-center group py-1"
                      >
                        <span className="group-hover:translate-x-1 transition-transform duration-300">{link.name}</span>
                        <ExternalLink className="h-3 w-3 ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Services Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-6 text-center"
            >
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-6 relative">
                  Services
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-primary-600 to-accent-600 rounded-full"></div>
                </h4>
                <ul className="space-y-3">
                  {footerLinks.services.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.path}
                        className="text-gray-700 hover:text-primary-600 transition-colors duration-300 text-sm flex items-center justify-center group py-1"
                      >
                        <span className="group-hover:translate-x-1 transition-transform duration-300">{link.name}</span>
                        <ExternalLink className="h-3 w-3 ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Support Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="space-y-6 text-center"
            >
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-6 relative">
                  Support
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-primary-600 to-accent-600 rounded-full"></div>
                </h4>
                <ul className="space-y-3">
                  {footerLinks.support.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.path}
                        className="text-gray-700 hover:text-primary-600 transition-colors duration-300 text-sm flex items-center justify-center group py-1"
                      >
                        <span className="group-hover:translate-x-1 transition-transform duration-300">{link.name}</span>
                        <ExternalLink className="h-3 w-3 ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>


        {/* Bottom Bar */}
        <div className="border-t border-gray-300 py-8">
          <div className="text-center space-y-2">
            <p className="text-gray-800 text-sm font-medium">
              &copy; {new Date().getFullYear()} Lumbini Consultancy Services Pvt. Ltd. All Rights Reserved.
            </p>
            <p className="text-gray-800 text-sm font-medium">
              | Designed and developed by <a href="https://saproh.com/" target="_blank" rel="noopener noreferrer" className="text-lumbini-blue hover:text-lumbini-cyan transition-colors duration-300 font-semibold">SAPROH</a>
            </p>

          </div>
        </div>

        {/* Floating Back to Top Button */}
        <motion.button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center group z-50 border border-slate-600/30 hover:border-slate-500/50"
          whileHover={{ scale: 1.1, y: -2 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.5 }}
        >
          <ArrowUp className="h-6 w-6 group-hover:-translate-y-0.5 transition-transform duration-300" />
        </motion.button>
      </div>
    </footer>
  );
};

export default Footer;